<template>
  <v-form ref="CONTACT" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="CONTACT">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <civilite-select v-if="isDisplayed('CIVILITE_ID')"
                         ref="CIVILITE_ID"
                         :theme="theme"
                         :showMissing="showMissing"
                         v-model:value=civiliteId
                         :contact=contact
                         :moduleId=moduleId
                         libelle="'Civilité'"
                         newLabel=''
                         selectText=""
                         @status=statusCiviliteId
                         :requiredAttributs="requiredAttributs"
                         :autoSelect=true
                         :ressource=ressourceCiviliteSelect
                         :filters=civiliteIdFilter
                         :toggleReset=resetCiviliteId
                         :required="isRequired('CIVILITE_ID')"
                         :contextId=contact.id
                         :readOnly="readOnly('CIVILITE_ID')"
                         :objType=objType
                         :fiche=fiche
                         :sectionName="sectionName">
        </civilite-select>
        <contact-prenom v-if="isDisplayed('PRENOM')"
                        ref="PRENOM"
                        :theme="theme"
                        :showMissing="showMissing"
                        v-model:value=prenom
                        :min="3"
                        :max="80"
                        @status=statusPrenom
                        :over-ride-placeholder=placeholderPrenom
                        :required="isRequired('PRENOM')"
                        :contextId=contact.id
                        :readOnly="readOnly('PRENOM')"
                        :objType=objType
                        :fiche=fiche
                        :sectionName="sectionName">
        </contact-prenom>
        <contact-nom v-if="isDisplayed('NOM')"
                     ref="NOM"
                     :theme="theme"
                     :showMissing="showMissing"
                     v-model:value=nom
                     :min="3"
                     :max="80"
                     @status=statusNom
                     :over-ride-placeholder=placeholderNom
                     :required="isRequired('NOM')"
                     :contextId=contact.id
                     :readOnly="readOnly('NOM')"
                     :objType=objType
                     :fiche=fiche
                     :sectionName="sectionName">
        </contact-nom>
        <contact-mail v-if="isDisplayed('MAIL')"
                      ref="MAIL"
                      :theme="theme"
                      :showMissing="showMissing"
                      v-model:value=mail
                      @status=statusMail
                      :over-ride-placeholder=placeholderMail
                      :required="isRequired('MAIL')"
                      :contextId=contact.id
                      :readOnly="readOnly('MAIL')"
                      :objType=objType
                      :fiche=fiche
                      :sectionName="sectionName">
        </contact-mail>
        <contact-numero v-if="isDisplayed('NUMERO')"
                        ref="NUMERO"
                        :theme="theme"
                        :showMissing="showMissing"
                        v-model:value=numero
                        @status=statusNumero
                        :required="isRequired('NUMERO')"
                        :contextId=contact.id
                        :readOnly="readOnly('NUMERO')"
                        :objType=objType
                        :fiche=fiche
                        :sectionName="sectionName">
        </contact-numero>
        <qualite-select v-if="isDisplayed('QUALITE_ID')"
                        ref="QUALITE_ID"
                        :theme="theme"
                        :showMissing="showMissing"
                        v-model:value=qualiteId
                        :contact=contact
                        :moduleId=moduleId
                        libelle="'Qualité'"
                        newLabel=''
                        selectText=""
                        @status=statusQualiteId
                        :requiredAttributs="requiredAttributs"
                        :autoSelect=true
                        :ressource=ressourceQualiteSelect
                        :filters=qualiteIdFilter
                        :toggleReset=resetQualiteId
                        :required="isRequired('QUALITE_ID')"
                        :contextId=contact.id
                        :readOnly="readOnly('QUALITE_ID')"
                        :objType=objType
                        :fiche=fiche
                        :sectionName="sectionName">
        </qualite-select>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudContact from '@/generated/cruds/CrudContact';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudContact,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.CONTACT,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderPrenom: null,
        placeholderNom: null,
        placeholderMail: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceCiviliteSelect () { return RessourceGenControl(GenControl.CONTACT_CIVILITE_SELECT); },
      ressourceQualiteSelect () { return RessourceGenControl(GenControl.CONTACT_QUALITE_SELECT); },

      ressource () { return RessourceGenSection(GenSection.CONTACT); },

      fields (): string[] {
        return ['CIVILITE_ID', 'PRENOM', 'NOM', 'MAIL', 'NUMERO', 'QUALITE_ID'];
      },

      extraButtons (): Button[] {
        return [];
      },

      civiliteIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
      qualiteIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editContact.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("CIVILITE_ID", false);
          this.readOnlyMap.set("PRENOM", false);
          this.readOnlyMap.set("NOM", false);
          this.readOnlyMap.set("MAIL", false);
          this.readOnlyMap.set("NUMERO", false);
          this.readOnlyMap.set("QUALITE_ID", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("CIVILITE_ID", this.requiredMissing(requiredAttributs, "CIVILITE_ID") || !!this.fiche);
          this.displayMap.set("PRENOM", true);
          this.displayMap.set("NOM", true);
          this.displayMap.set("MAIL", this.requiredMissing(requiredAttributs, "MAIL") || !!this.fiche);
          this.displayMap.set("NUMERO", this.requiredMissing(requiredAttributs, "NUMERO") || !!this.fiche);
          this.displayMap.set("QUALITE_ID", this.requiredMissing(requiredAttributs, "QUALITE_ID") || !!this.fiche);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("CIVILITE_ID", this.requiredMissing(requiredAttributs, "CIVILITE_ID") || !this.fiche);
          this.requiredMap.set("PRENOM", true);
          this.requiredMap.set("NOM", true);
          this.requiredMap.set("MAIL", this.requiredMissing(requiredAttributs, "MAIL") || !this.fiche);
          this.requiredMap.set("NUMERO", this.requiredMissing(requiredAttributs, "NUMERO") || !this.fiche);
          this.requiredMap.set("QUALITE_ID", this.requiredMissing(requiredAttributs, "QUALITE_ID") || !this.fiche);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoContact' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
